/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import React, { useCallback, useEffect, useState } from "react";

import { useLocation, useParams, useSearchParams } from "react-router-dom";
import useStateRef from "react-usestateref";

import { Nullable, TObject } from "constants/interfaces";

import useTryCatch from "hooks/useTryCatch";

import LoadingScreen from "components/ui/LoadingScreen/LoadingScreen";

import { FetchError, FetchSuccess, toJSON } from "lib";
import { ErrorPage, ServerError } from "pages";
import { useGeneralState } from "state";

interface IProps {
  fetcher: (params: any, searchParams?: any) => any;
  children: React.ReactNode;
  onSuccess?: (data: any) => any;
  forceFetch?: boolean;
  onlyOnFirstRender?: boolean;
}

export const DynamicRoutesManager = ({
  children,
  fetcher,
  onSuccess,
  forceFetch = false,
  onlyOnFirstRender = false,
}: IProps): JSX.Element => {
  const {
    // @ts-ignore
    // isFirstPageToRender,
    // @ts-ignore
    invalidRoutes,
    // @ts-ignore
    addInvalidRoute,
    // @ts-ignore
    addValidRoute,
    // @ts-ignore
    validRoutes,
  } = useGeneralState();

  const params = useParams();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<Nullable<TObject>>(null);

  const DataService = {
    // The reason I am assigning data with pathname as the key is because sometimes
    // the data would be shared between two different routes in a very confusing way!
    // LATER FIGURED THE CAUSE: provide `key` prop to each instance of DynamicRoutesManager
    get: () => data?.[pathname],
    set: (data: any) => {
      setData((prev) => ({
        ...prev,
        [pathname]: data,
      }));
    },
  };

  const [, setIsFirstPageToRender, { current: isFirstPageToRender }] =
    useStateRef(true);

  const _fetcher = useTryCatch(
    async () => {
      const req = await fetcher(
        params,
        Object.fromEntries(searchParams.entries()),
      );

      if (req.ok !== false) {
        if (req instanceof FetchSuccess) {
          DataService.set(req.data);
          onSuccess?.(req.data);
        } else {
          const data = await toJSON(req);
          DataService.set(data);
          onSuccess?.(data);
        }

        addValidRoute(pathname);
      } else {
        throw req;
      }

      return req;
    },
    {
      setLoading: (state) => setIsLoading(state),
      onError: (req: unknown) => {
        if (req instanceof FetchError) {
          if (req.error === "ABORTED") return;
          addInvalidRoute({ name: pathname, error: req.statusCode });
        } else {
          addInvalidRoute({
            name: pathname,
            // @ts-ignore
            error: req?.status || 404,
          });
        }
      },
    },
  );

  const fetchToValidateRoute = useCallback(_fetcher, [_fetcher]);
  useEffect(() => {
    if (onlyOnFirstRender && !isFirstPageToRender) return setIsLoading(false);

    if (!invalidRoutes.hasOwnProperty(pathname)) {
      // if has (page) in params, then it's a pagination route. so we don't need to fetch data unless it is on first render.
      if (
        (params.hasOwnProperty("page") &&
          !isFirstPageToRender &&
          !forceFetch) ||
        (validRoutes[pathname] && !forceFetch && onlyOnFirstRender)
      ) {
        return setIsLoading(false);
      }

      fetchToValidateRoute();
    } else {
      setIsLoading(false);
    }

    if (isFirstPageToRender) {
      setIsFirstPageToRender(false);
    }
  }, [pathname]);

  if (isLoading) {
    return <LoadingScreen test-id="821p9JP_" />;
  }

  if (invalidRoutes.hasOwnProperty(pathname)) {
    // 400, 404...etc
    if (invalidRoutes[pathname] < 500 && invalidRoutes[pathname] > 399) {
      return <ErrorPage test-id="MJ_z1srR" />;
    } else {
      return <ServerError test-id="400QE5Ej" />;
    }
  }

  return (
    (
      <>
        {React.Children.map(children, (child) => {
          // pass data to children, explicitly forms.
          return React.cloneElement(child as React.ReactElement<any>, {
            data: DataService.get(),
          });
        })}
      </>
    ) || null
  );
};

export default DynamicRoutesManager;
